import { useState, useEffect, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Link2, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LinkPopoverProps {
  editor: Editor | null;
  className?: string;
}

interface LinkState {
  url: string;
  displayText: string;
  openInNewTab: boolean;
}

function validateURL(url: string): { valid: boolean; error?: string; suggestion?: string } {
  if (!url.trim()) {
    return { valid: false, error: 'URL is required' };
  }

  // Check for common patterns
  const patterns = {
    http: /^https?:\/\/.+/i,
    mailto: /^mailto:.+@.+/i,
    tel: /^tel:\+?[\d-]+/i,
    relative: /^\/[^\s]*/,
    hash: /^#[^\s]*/,
  };

  const isValid = Object.values(patterns).some(pattern => pattern.test(url));

  if (!isValid) {
    // Suggest adding https://
    if (!url.includes('://') && !url.startsWith('#') && !url.startsWith('/')) {
      return {
        valid: false,
        error: 'Invalid URL format',
        suggestion: `https://${url}`,
      };
    }
    return { valid: false, error: 'Invalid URL format' };
  }

  return { valid: true };
}

export function LinkPopover({ editor, className }: LinkPopoverProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<LinkState>({
    url: '',
    displayText: '',
    openInNewTab: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Load existing link data when popover opens
  useEffect(() => {
    if (open && editor) {
      const { href, target } = editor.getAttributes('link');
      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to, '');

      if (href) {
        // Editing existing link
        setIsEditing(true);
        setState({
          url: href,
          displayText: selectedText,
          openInNewTab: target === '_blank',
        });
      } else {
        // Creating new link
        setIsEditing(false);
        setState({
          url: '',
          displayText: selectedText,
          openInNewTab: false,
        });
      }
      setError(null);
    }
  }, [open, editor]);

  // Keyboard shortcut (Ctrl+K / Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleApply = useCallback(() => {
    if (!editor) return;

    const validation = validateURL(state.url);
    if (!validation.valid) {
      setError(validation.error || 'Invalid URL');
      return;
    }

    const attrs: { href: string; target?: string; rel?: string } = {
      href: state.url,
    };

    if (state.openInNewTab) {
      attrs.target = '_blank';
      attrs.rel = 'noopener noreferrer';
    }

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink(attrs)
      .run();

    setOpen(false);
  }, [editor, state]);

  const handleRemove = useCallback(() => {
    if (!editor) return;

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .unsetLink()
      .run();

    setOpen(false);
  }, [editor]);

  const handleUrlChange = (url: string) => {
    setState(prev => ({ ...prev, url }));
    setError(null);
  };

  const applySuggestion = () => {
    const validation = validateURL(state.url);
    if (validation.suggestion) {
      setState(prev => ({ ...prev, url: validation.suggestion! }));
      setError(null);
    }
  };

  const isActive = editor?.isActive('link');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn("h-8 w-8 p-0", isActive && "bg-muted", className)}
          aria-label="Insert or edit link"
          title="Insert link (Ctrl+K)"
        >
          <Link2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">
              {isEditing ? 'Edit Link' : 'Insert Link'}
            </h4>
          </div>

          <div className="space-y-3">
            {/* URL Input */}
            <div className="space-y-2">
              <Label htmlFor="link-url">URL</Label>
              <Input
                id="link-url"
                placeholder="https://example.com"
                value={state.url}
                onChange={(e) => handleUrlChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleApply();
                  }
                  if (e.key === 'Escape') {
                    setOpen(false);
                  }
                }}
                autoFocus
                className={cn(error && "border-destructive")}
              />
              {error && (
                <div className="text-sm text-destructive">
                  {error}
                  {validateURL(state.url).suggestion && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 ml-1 text-destructive underline"
                      onClick={applySuggestion}
                    >
                      Use {validateURL(state.url).suggestion}?
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Display Text (read-only when text is selected) */}
            {state.displayText && (
              <div className="space-y-2">
                <Label htmlFor="link-text">Link Text</Label>
                <Input
                  id="link-text"
                  value={state.displayText}
                  disabled
                  className="bg-muted"
                />
              </div>
            )}

            {/* Open in new tab checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="link-new-tab"
                checked={state.openInNewTab}
                onCheckedChange={(checked) =>
                  setState(prev => ({ ...prev, openInNewTab: checked === true }))
                }
              />
              <Label htmlFor="link-new-tab" className="text-sm font-normal cursor-pointer">
                Open in new tab
              </Label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button onClick={handleApply} size="sm">
              Apply
            </Button>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            {isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemove}
                className="ml-auto text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Remove
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
